import {supabase} from '../lib/supabase';
import {getGroupMembersAvailability, convertBusyTimesToAvailable} from './availabilityService';
import {getAvailabilityPreference} from './userPreferencesService';
import {getWeatherForTimeSlots, isWeatherAvailable, RateLimitError, formatResetTime} from './weatherService';
import {getGolfCourseCoordinates} from './geocodingService';
import {requestGroupAvailability} from './availabilityRequestService';
import {autoSyncRoundToOutlook, updateOutlookEvent, deleteOutlookEvent} from './outlookEventService';
import {autoSyncRoundToGoogle, updateGoogleEvent, deleteGoogleEvent} from './googleEventService';
import {getConnectedProvider} from './calendarSyncService';
import {calculateDaylightScore, getSunTimes} from './sunService';
import {resolveTimezoneFromCoords, roundStartInstant} from '../utils/timeHelpers';
import {COMPOSITE_WEIGHTS, MEMBER_CURVE, COMPOSITE_SCORE_CAP, DAYLIGHT_CONFIG} from './scoringConfig.js';
import {recordPositiveAction} from './reviewPromptService';
import {formatClubDisplayName} from '../utils/clubDisplay';

/**
 * Pull lat/lng out of whatever the create-round form handed us. Used at
 * INSERT time so `rounds.course_latitude`, `rounds.course_longitude`, and
 * `rounds.timezone` are populated immediately — otherwise the auto-sync to
 * Outlook/Google reads the row before the background weather fetch backfills
 * them and the calendar event gets stored with `timeZone: 'UTC'` (silent
 * wrong-time bug for any non-UTC user).
 */
const extractRoundCoordinates = (roundData) => {
  const candidates = [
    roundData?.golfClub,
    roundData?.golfCourse,
    roundData?.course,
  ];
  for (const c of candidates) {
    if (!c || typeof c !== 'object') continue;
    // Top-level lat/lng (golf_clubs row shape: { latitude, longitude }).
    // Also accept the smart-schedule result shape which nests coords:
    //   { name, coordinates: { lat, lon }, hasWeatherData }
    const lat = c.latitude ?? c.lat ?? c.coordinates?.lat ?? c.coordinates?.latitude;
    const lng = c.longitude ?? c.lng ?? c.lon ?? c.coordinates?.lon ?? c.coordinates?.lng ?? c.coordinates?.longitude;
    if (lat != null && lng != null) {
      return { lat: Number(lat), lng: Number(lng) };
    }
  }
  return null;
};

const resolveRoundInsertTzFields = (roundData) => {
  const coords = extractRoundCoordinates(roundData);
  if (!coords) return {};
  const tz = resolveTimezoneFromCoords(coords.lat, coords.lng);
  return {
    course_latitude: coords.lat,
    course_longitude: coords.lng,
    ...(tz ? { timezone: tz } : {}),
  };
};

/**
 * Resolve whatever the caller handed us (a golf_clubs row, a partial object,
 * or a string) into a single display string for the denormalised
 * `rounds.course` column. For golf_clubs rows this includes the course suffix
 * for multi-course clubs (e.g. "The Belfry - Brabazon Course").
 */
const resolveRoundCourseString = (courseData) => {
  if (!courseData) return null;
  if (typeof courseData === 'string') return courseData;
  if (typeof courseData === 'object') {
    const formatted = formatClubDisplayName(courseData);
    if (formatted) return formatted;
    if (courseData.name) return courseData.name;
  }
  return null;
};

/**
 * Auto-sync a round to the connected calendar (Outlook or Google)
 * @param {string} roundId - The round ID to sync
 * @returns {Promise<boolean>} - True if sync was attempted, false if no calendar connected
 */
const autoSyncRoundToCalendar = async (roundId) => {
  try {
    const provider = await getConnectedProvider();
    if (!provider) {
      console.log('No calendar connected, skipping round sync');
      return false;
    }

    if (provider === 'google') {
      await autoSyncRoundToGoogle(roundId);
    } else {
      await autoSyncRoundToOutlook(roundId);
    }
    return true;
  } catch (error) {
    console.error('Error syncing round to calendar:', error);
    return false;
  }
};

/**
 * Update a calendar event for a round (Outlook or Google)
 * @param {string} roundId - The round ID to update
 */
const updateCalendarEvent = async (roundId) => {
  try {
    const provider = await getConnectedProvider();
    if (!provider) return;

    if (provider === 'google') {
      await updateGoogleEvent(roundId);
    } else {
      await updateOutlookEvent(roundId);
    }
  } catch (error) {
    console.error('Error updating calendar event:', error);
  }
};

/**
 * Delete a calendar event for a round (Outlook or Google)
 * @param {string} roundId - The round ID to delete
 */
const deleteCalendarEvent = async (roundId) => {
  try {
    const provider = await getConnectedProvider();
    if (!provider) return;

    if (provider === 'google') {
      await deleteGoogleEvent(roundId);
    } else {
      await deleteOutlookEvent(roundId);
    }
  } catch (error) {
    console.error('Error deleting calendar event:', error);
  }
};

/**
 * Fetch weather data for a round and store it directly on the rounds table
 * This is non-blocking - weather failure should not prevent round creation
 * @param {number} roundId - The round ID
 * @param {string} courseName - Name of the golf course
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format
 * @param {number} holeCount - Number of holes (9 or 18)
 * @returns {Promise<object|null>} - Weather data if successful, null otherwise
 */
const fetchAndStoreRoundWeather = async (roundId, courseName, date, time, holeCount = 18) => {
  try {
    console.log(`🌤️ Fetching weather for round ${roundId}: ${courseName} on ${date} at ${time}`);

    // Skip if no course or placeholder course
    if (!courseName ||
        courseName.toLowerCase() === 'tbd' ||
        courseName.toLowerCase() === 'to be determined' ||
        courseName.toLowerCase() === 'custom course') {
      console.log('⏭️ Skipping weather fetch - no valid course name');
      return null;
    }

    // Get coordinates from golf_clubs table or Google Places
    const coordinates = await getGolfCourseCoordinates(courseName);
    if (!coordinates || !coordinates.lat || !coordinates.lon) {
      console.log('⏭️ Skipping weather fetch - no coordinates found for course');
      await supabase
        .from('rounds')
        .update({
          weather_fetch_error: 'No coordinates found for course',
          weather_updated_at: new Date().toISOString()
        })
        .eq('id', roundId);
      return null;
    }

    // Check if weather is available for these coordinates
    if (!isWeatherAvailable(coordinates.lat, coordinates.lon)) {
      console.log('⏭️ Skipping weather fetch - coordinates outside coverage area');
      await supabase
        .from('rounds')
        .update({
          course_latitude: coordinates.lat,
          course_longitude: coordinates.lon,
          timezone: resolveTimezoneFromCoords(coordinates.lat, coordinates.lon),
          weather_fetch_error: 'Location outside weather coverage area',
          weather_updated_at: new Date().toISOString()
        })
        .eq('id', roundId);
      return null;
    }

    // Build a canonical UTC instant for the tee time using the course's
    // resolved IANA tz. Without this both the picker (browser-local) and the
    // server-side refresh (Deno UTC) would interpret `${date}T${time}`
    // differently and pull weather for different hours.
    const instant = roundStartInstant({
      date,
      time,
      course_latitude: coordinates.lat,
      course_longitude: coordinates.lon,
    });
    const dateTimeStr = instant ? instant.toISOString() : `${date}T${time.length === 5 ? `${time}:00` : time}`;
    const timeSlot = {
      startTime: dateTimeStr,
      datetime: dateTimeStr,
      date: date
    };

    // Fetch weather using existing tiered approach
    const durationHours = holeCount === 9 ? 2 : 4;
    const weatherResults = await getWeatherForTimeSlots(
      coordinates.lat,
      coordinates.lon,
      [timeSlot],
      { durationHours }
    );

    const result = weatherResults?.[0];
    const weather = result?.weather;

    if (!weather) {
      console.log('⏭️ No weather data returned from API');
      await supabase
        .from('rounds')
        .update({
          course_latitude: coordinates.lat,
          course_longitude: coordinates.lon,
          timezone: resolveTimezoneFromCoords(coordinates.lat, coordinates.lon),
          weather_fetch_error: 'No weather data returned from API',
          weather_updated_at: new Date().toISOString()
        })
        .eq('id', roundId);
      return null;
    }

    // Determine accuracy based on data source
    let accuracy = 'medium';
    if (result.dataSource === 'hourly') {
      accuracy = 'high';
    } else if (result.dataSource === 'daily' || result.dataSource === 'onecall-daily') {
      accuracy = 'low';
    }

    // Extract daylight info from golfScore
    const daylightFactor = weather.golfScore?.factors?.find(f => f.factor === 'daylight');
    const sunTimes = weather.golfScore?.sunTimes;

    // sunTimes.sunriseUTC / sunsetUTC are true UTC Date objects from SunCalc.
    // Display strings sunTimes.sunrise / sunset are already local at the course
    // (see sunService.js), but for persistence we want the canonical UTC ISO.
    let sunriseISO = null;
    let sunsetISO = null;
    let daylightHours = null;

    if (sunTimes?.sunriseUTC && sunTimes?.sunsetUTC) {
      sunriseISO = sunTimes.sunriseUTC.toISOString();
      sunsetISO = sunTimes.sunsetUTC.toISOString();
      daylightHours = Math.round(
        (sunTimes.sunsetUTC.getTime() - sunTimes.sunriseUTC.getTime()) / (1000 * 60 * 60) * 10
      ) / 10;
    }

    // Store weather data directly on the rounds table
    const { error } = await supabase
      .from('rounds')
      .update({
        course_latitude: coordinates.lat,
        course_longitude: coordinates.lon,
        timezone: resolveTimezoneFromCoords(coordinates.lat, coordinates.lon),
        weather_temperature: weather.temperature,
        weather_wind_speed: weather.windSpeed,
        weather_precipitation: weather.precipitation,
        weather_humidity: weather.humidity,
        weather_uv_index: weather.uvIndex ?? null,
        weather_golf_score: weather.golfScore?.score,
        weather_golf_category: weather.golfScore?.category,
        weather_is_unplayable: weather.isUnplayable || false,
        weather_ground_category: weather.groundQuality?.category,
        weather_data_source: result.dataSource,
        weather_accuracy: accuracy,
        // New precipitation source columns
        weather_precipitation_source: weather.precipitationSource || result.dataSource,
        weather_precipitation_label: weather.precipitationLabel || (
          result.dataSource === 'hourly' ? 'Rain during round' : 'Rain on the day'
        ),
        // New daylight columns
        weather_daylight_hours: daylightHours,
        weather_sunrise: sunriseISO,
        weather_sunset: sunsetISO,
        weather_daylight_score: daylightFactor?.score || null,
        weather_daylight_category: daylightFactor?.category || weather.golfScore?.daylightCategory || null,
        weather_alerts: weather.alerts?.length > 0 ? weather.alerts : null,
        weather_updated_at: new Date().toISOString(),
        weather_fetch_error: null
      })
      .eq('id', roundId);

    if (error) {
      console.error('❌ Error storing round weather:', error);
      return null;
    }

    console.log(`✅ Weather stored for round ${roundId}: ${weather.golfScore?.category} (${weather.golfScore?.score?.toFixed(1)})`);
    return weather;
  } catch (error) {
    console.error('❌ Error fetching weather for round:', error);
    // Store the error for debugging
    try {
      await supabase
        .from('rounds')
        .update({
          weather_fetch_error: error.message || 'Unknown error',
          weather_updated_at: new Date().toISOString()
        })
        .eq('id', roundId);
    } catch (updateError) {
      console.error('Failed to store weather error:', updateError);
    }
    return null;
  }
};

/**
 * Trigger a fresh weather reading for an existing round via the
 * refresh-round-weather edge function. The function persists the reading to
 * the rounds row itself, so callers should re-read the round after this
 * resolves. Throws RateLimitError when the per-user weather budget is spent so
 * the UI can show a friendly "try again in X" message.
 *
 * Returns the edge-function result: { ok, skipped?, error?, ... }.
 */
export const refreshRoundWeather = async (roundId) => {
  const numericId = Number(roundId);
  if (!numericId) {
    throw new Error('A valid round id is required');
  }

  const rateLimited = (body) => body?.error === 'Rate limit exceeded';
  const throwRateLimit = (body) => {
    throw new RateLimitError(
      `Weather refresh limit reached. Try again ${formatResetTime(body.reset_at)}.`,
      body.reset_at,
      body.current_usage,
      body.limits
    );
  };

  const { data, error } = await supabase.functions.invoke('refresh-round-weather', {
    body: { round_id: numericId },
  });

  // The edge function returns a 429 body on the per-user rate-limit backstop.
  // supabase-js surfaces non-2xx as `error` (FunctionsHttpError) carrying the
  // body on error.context, but can also parse it into `data`; check both.
  if (rateLimited(data)) throwRateLimit(data);

  if (error) {
    let body = null;
    try {
      body = await error.context?.json?.();
    } catch {
      body = null;
    }
    if (rateLimited(body)) throwRateLimit(body);
    throw new Error(body?.error || error.message || 'Weather refresh failed');
  }

  return data || { ok: false, error: 'No response from weather refresh' };
};

/**
 * Get the booking website URL for a golf course
 * Looks up from golf_clubs table by name, falls back to Google Maps URI
 * @param {string} courseName - The course name to look up
 * @returns {Promise<{website: string|null, googleMapsUri: string|null, phone: string|null}>}
 */
export const getCourseWebsite = async (courseName) => {
  if (!courseName || courseName === 'To be determined') {
    return { website: null, googleMapsUri: null, phone: null };
  }

  try {
    // Exact match first
    let { data, error } = await supabase
      .from('golf_clubs')
      .select('website, google_maps_uri, phone')
      .eq('name', courseName)
      .single();

    // Fuzzy match if no exact match found
    if (!data && error?.code === 'PGRST116') {
      const { data: fuzzyData } = await supabase
        .from('golf_clubs')
        .select('website, google_maps_uri, phone')
        .ilike('name', `%${courseName}%`)
        .limit(1);

      if (fuzzyData?.length > 0) {
        data = fuzzyData[0];
      }
    }

    return {
      website: data?.website || null,
      googleMapsUri: data?.google_maps_uri || null,
      phone: data?.phone || null
    };
  } catch (error) {
    console.error('Error fetching course website:', error);
    return { website: null, googleMapsUri: null, phone: null };
  }
};

// Helper function to capitalize words in a name
const capitalizeWords = (str) => {
  if (!str) return '';
  
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (word.length === 0) return '';
      // Handle special cases like O'Brien, McDonald, etc.
      if (word.includes("'")) {
        const parts = word.split("'");
        return parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("'");
      }
      if (word.startsWith('mc') && word.length > 2) {
        return 'Mc' + word.charAt(2).toUpperCase() + word.slice(3);
      }
      if (word.startsWith('mac') && word.length > 3) {
        return 'Mac' + word.charAt(3).toUpperCase() + word.slice(4);
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Helper function to get user's preference type from database
const getUserPreferenceType = async (userId) => {
  try {
    // If no userId provided, get current user's preference
    if (!userId) {
      return await getAvailabilityPreference();
    }

    // For other users, we need to query the database directly
    // This is used in group contexts where we need other users' preferences
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preference_value')
      .eq('user_id', userId)
      .eq('preference_key', 'availability_preference')
      .single();
    
    // Handle 406 (Not Acceptable) errors - EXPECTED BEHAVIOR
    // 406 errors occur when Row Level Security (RLS) policies prevent access to other users' preferences
    // This is intentional security - users can only read their own preferences, not other users'
    // These errors appear in browser console but are handled gracefully
    if (error && error.status === 406) {
      // Return default 'available' preference for other users when RLS blocks access
      return 'available';
    }
    
    // Handle no rows found error
    if (error && error.code === 'PGRST116') {
      return 'available';
    }
    
    // Handle other errors
    if (error) {
      console.warn('Could not fetch user preference from database:', error);
      return 'available';
    }
    
    return data?.preference_value || 'available';
  } catch (error) {
    console.warn('Could not access user preference:', error);
    return 'available';
  }
};

// Helper function to check if a user has calendar-synced availability data
// Uses the hasCalendarSynced flag from availabilityData (set by getGroupMembersAvailability)
// This avoids RLS issues with querying calendar_connections table
const isUserCalendarConnected = (userId, availabilityData) => {
  if (!userId) {
    return false;
  }
  const userData = availabilityData[userId];
  return userData?.hasCalendarSynced || false;
};

// Generate sliding time windows based on hole count.
// Sweeps the full 24h day so every timezone / season is covered; the
// daylight-aware composite scoring (calculateCompositeScore + the zero-daylight
// safety-net filter) suppresses windows that fall outside playable daylight.
const generateTimeWindows = (holeCount = 18) => {
  const windows = [];
  const startHour = 0; // midnight

  // Determine window duration based on hole count
  const windowDuration = holeCount === 9 ? 2 : 4; // 2 hours for 9 holes, 4 hours for 18 holes

  // Last window must finish by the end of the day (24:00)
  const endHour = 24 - windowDuration;

  for (let hour = startHour; hour <= endHour; hour++) {
    const startTime = `${String(hour).padStart(2, '0')}:00`;
    const endTime = `${String(hour + windowDuration).padStart(2, '0')}:00`;
    windows.push({
      start: startTime,
      end: endTime,
      label: formatTimeWindow(startTime, endTime)
    });
  }

  return windows;
};

const formatTimeWindow = (start, end) => {
  const formatTime = (timeString) => {
    const [hours] = timeString.split(':').map(Number);
    // 24:00 is end-of-day midnight; render it (and 00:00) as 12AM, not 12PM.
    const period = hours >= 12 && hours < 24 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}${period}`;
  };

  return `${formatTime(start)} - ${formatTime(end)}`;
};

// Check if two time ranges overlap
// Exported for reuse in golfNowService.js
export const timeRangesOverlap = (range1, range2) => {
  const [start1Hours, start1Minutes] = range1.start.split(':').map(Number);
  const [end1Hours, end1Minutes] = range1.end.split(':').map(Number);
  const [start2Hours, start2Minutes] = range2.start.split(':').map(Number);
  const [end2Hours, end2Minutes] = range2.end.split(':').map(Number);

  const start1Total = start1Hours * 60 + start1Minutes;
  const end1Total = end1Hours * 60 + end1Minutes;
  const start2Total = start2Hours * 60 + start2Minutes;
  const end2Total = end2Hours * 60 + end2Minutes;

  return start1Total < end2Total && start2Total < end1Total;
};

// Calculate overlap duration in minutes - exported for reuse in golfNowService.js
export const calculateOverlapDuration = (window, userRange) => {
  const [windowStartHours, windowStartMinutes] = window.start.split(':').map(Number);
  const [windowEndHours, windowEndMinutes] = window.end.split(':').map(Number);
  const [userStartHours, userStartMinutes] = userRange.start.split(':').map(Number);
  const [userEndHours, userEndMinutes] = userRange.end.split(':').map(Number);

  const windowStartTotal = windowStartHours * 60 + windowStartMinutes;
  const windowEndTotal = windowEndHours * 60 + windowEndMinutes;
  const userStartTotal = userStartHours * 60 + userStartMinutes;
  const userEndTotal = userEndHours * 60 + userEndMinutes;

  const overlapStart = Math.max(windowStartTotal, userStartTotal);
  const overlapEnd = Math.min(windowEndTotal, userEndTotal);

  return Math.max(0, overlapEnd - overlapStart);
};

// Get all member IDs for a group
const getAllGroupMemberIds = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('group_members')
      .select('user_id, member_name, role')
      .eq('group_id', groupId);

    if (error) {
      console.error('Error in getAllGroupMemberIds:', error);
      throw error;
    }

    // Ensure unique user IDs to prevent duplicate invitations
    // Filter out null/undefined values and ensure uniqueness
    const userIds = data
      .map(member => member.user_id)
      .filter(id => id !== null && id !== undefined);

    // Use Set to guarantee uniqueness
    const uniqueUserIds = [...new Set(userIds)];

    return uniqueUserIds;
  } catch (error) {
    console.error('Exception in getAllGroupMemberIds:', error);
    throw error;
  }
};

// Generate dates within the specified range only
const generateDatesInRange = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const endDateObj = new Date(endDate);

  // Set to midnight for date-only comparison
  currentDate.setHours(0, 0, 0, 0);
  endDateObj.setHours(23, 59, 59, 999);

  while (currentDate <= endDateObj) {
    // Use local timezone formatting to avoid UTC conversion
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

// Filter availability dates to only include those within the requested range
const filterAvailabilityDatesToRange = (availabilityDates, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set to midnight for date-only comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return availabilityDates.filter(dateString => {
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date >= start && date <= end;
  });
};

// Filter slots to only include those within the date range
const filterSlotsToDateRange = (slots, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set to midnight for date-only comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return slots.filter(slot => {
    const slotDate = new Date(slot.date);
    slotDate.setHours(0, 0, 0, 0);
    return slotDate >= start && slotDate <= end;
  });
};

// ENHANCED: Main function with proper error handling and timeout protection
export const calculateOptimalTimeSlots = async (groupId, dateRange, selectedMemberIds = [], holeCount = 18, golfClub = null, currentUserId = null) => {
  const overallStartTime = Date.now();
  
  try {
    // Set up a timeout promise for the entire operation (60 seconds - increased for better reliability)
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => {
        const elapsed = Date.now() - overallStartTime;
        reject(new Error(`Smart scheduling analysis timed out after ${elapsed}ms. Consider reducing the date range (currently ${dateRange.startDate} to ${dateRange.endDate}) or number of members (currently ${selectedMemberIds.length}).`));
      }, 60000)
    );

    const analysisPromise = performAnalysis(groupId, dateRange, selectedMemberIds, holeCount, golfClub, currentUserId);

    // Race the analysis against the timeout
    const result = await Promise.race([analysisPromise, timeoutPromise]);
    
    const totalTime = Date.now() - overallStartTime;
    
    return result;
  } catch (error) {
    const totalTime = Date.now() - overallStartTime;
    console.error(`❌ Smart scheduling failed after ${totalTime}ms:`, error.message);
    
    // Enhanced error categorization and user-friendly messages
    let userMessage = 'Smart scheduling failed';
    let suggestions = [];
    
    if (error.message.includes('timed out') || error.message.includes('timeout')) {
      userMessage = 'Analysis timed out';
      suggestions = [
        'Try reducing the date range (fewer days to analyse)',
        'Select fewer group members for analysis',
        'Check your internet connection',
        'Try again during off-peak hours'
      ];
    } else if (error.message.includes('availability data')) {
      userMessage = 'Could not fetch member availability';
      suggestions = [
        'Ensure all selected members have set their availability',
        'Check that the group has active members',
        'Try refreshing the page and logging in again'
      ];
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      userMessage = 'Network connection issue';
      suggestions = [
        'Check your internet connection',
        'Try again in a few moments',
        'Contact support if the issue persists'
      ];
    } else {
      suggestions = ['Try again with different parameters', 'Contact support if the issue persists'];
    }
    
    const errorResponse = {
      data: [],
      error: new Error(userMessage),
      details: {
        originalError: error.message,
        suggestions,
        analysisTime: totalTime,
        timestamp: new Date().toISOString()
      }
    };
    
    return errorResponse;
  }
};

// ENHANCED: Separated analysis logic for better timeout handling
const performAnalysis = async (groupId, dateRange, selectedMemberIds, holeCount, golfClub, currentUserId) => {
  const analysisStartTime = Date.now();
  

  // Validate inputs
  // For solo scheduling, groupId can be null but selectedMemberIds must be provided
  if (!groupId && (!selectedMemberIds || selectedMemberIds.length === 0)) {
    throw new Error('Group ID is required (or provide member IDs for solo scheduling)');
  }

  if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
    throw new Error('Valid date range is required');
  }

  // For group scheduling without explicit member selection, get all group members
  if (groupId && (!selectedMemberIds || selectedMemberIds.length === 0)) {
    selectedMemberIds = await getAllGroupMemberIds(groupId);
  }

  const validationTime = Date.now() - analysisStartTime;

  // Validate member IDs
  const invalidMemberIds = selectedMemberIds.filter(id => !id || typeof id !== 'string');
  if (invalidMemberIds.length > 0) {
    throw new Error(`Invalid member IDs found: ${JSON.stringify(invalidMemberIds)}`);
  }

  // ENHANCED: Fetch availability data with timeout protection and retry logic
  // NOTE: getGroupMembersAvailability now returns ALL preference types
  
  const startTime = Date.now();
  let availabilityData, error;
  
  // Retry logic with exponential backoff
  const maxRetries = 3;
  const baseTimeout = 30000; // Increased from 15s to 30s
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      
      const availabilityPromise = getGroupMembersAvailability(
        selectedMemberIds,
        dateRange.startDate,
        dateRange.endDate,
        groupId
      );

      const availabilityTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Availability data fetch timed out after ${baseTimeout}ms (attempt ${attempt})`)), baseTimeout)
      );

      const result = await Promise.race([
        availabilityPromise,
        availabilityTimeoutPromise
      ]);
      
      const fetchTime = Date.now() - startTime;
      
      availabilityData = result.data;
      error = result.error;
      break; // Success - exit retry loop
      
    } catch (fetchError) {
      const attemptTime = Date.now() - startTime;
      console.warn(`⚠️ Availability fetch attempt ${attempt} failed after ${attemptTime}ms:`, fetchError.message);
      
      if (attempt === maxRetries) {
        // Final attempt failed
        error = fetchError;
        console.error('❌ All availability fetch attempts failed');
      } else {
        // Wait before retry with exponential backoff
        const backoffDelay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Cap at 5s
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
  }

  if (error) {
    console.error('❌ Availability data fetch failed after all retries:', error.message);
    
    // Provide actionable error messages based on error type
    let userFriendlyMessage = 'Failed to fetch availability data';
    if (error.message.includes('timed out')) {
      userFriendlyMessage = 'Availability data fetch timed out. This may be due to high server load or network issues. Please try again with a smaller date range or fewer members.';
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      userFriendlyMessage = 'Network error while fetching availability data. Please check your internet connection and try again.';
    } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
      userFriendlyMessage = 'Authentication error. Please refresh the page and log in again.';
    } else if (error.message.includes('429') || error.message.includes('rate limit')) {
      userFriendlyMessage = 'Too many requests. Please wait a moment and try again.';
    }
    
    throw new Error(`${userFriendlyMessage}: ${error.message}`);
  }


  if (!availabilityData || Object.keys(availabilityData).length === 0) {
    return { data: [], error: new Error('No availability data found. Please ensure members have set their availability in their profiles.') };
  }

  const availabilityFetchTime = Date.now() - startTime;

  // ENHANCED: Process availability data to handle both calendar-connected users and preference types
  const processingStartTime = Date.now();
  const processedAvailabilityData = {};

  // Use the local-tz date enumeration to avoid UTC date-shift. The previous
  // inline implementation used .toISOString().split('T')[0] which returns a
  // UTC date — so a Sunday range that starts at 2026-05-30T23:00:00Z (which
  // is Sunday 00:00 BST) collapsed to ["2026-05-30"], i.e. Saturday only,
  // and the scheduler found zero Sunday slots even though the user picked
  // Sunday.
  const allRequestedDates = generateDatesInRange(dateRange.startDate, dateRange.endDate);

  for (const [userId, userData] of Object.entries(availabilityData)) {
    // Check if user has calendar-synced availability (takes priority over preference setting)
    const hasCalendarConnected = isUserCalendarConnected(userId, availabilityData);

    if (hasCalendarConnected) {
      // Calendar-connected users: data is ALWAYS busy times, convert to available
      const convertedAvailability = {};

      // First, convert existing busy times to available gaps
      if (userData.availability) {
        for (const [date, timeRanges] of Object.entries(userData.availability)) {
          if (timeRanges && timeRanges.length > 0) {
            convertedAvailability[date] = convertBusyTimesToAvailable(timeRanges);
          }
        }
      }

      // Fill in dates with NO records as fully available (full day)
      // This handles days where the user has no calendar events
      allRequestedDates.forEach(date => {
        if (!convertedAvailability[date]) {
          convertedAvailability[date] = [{ start: '00:00', end: '24:00' }];
        }
      });

      processedAvailabilityData[userId] = {
        ...userData,
        availability: convertedAvailability
      };
    } else {
      // Non-calendar user: use preference type logic
      if (!userData.availability || Object.keys(userData.availability).length === 0) {
        processedAvailabilityData[userId] = userData;
        continue;
      }

      // Check user's preference type from database or default to 'available'
      let userPreferenceType = 'available';
      try {
        userPreferenceType = await getUserPreferenceType(userId) || 'available';
      } catch (prefError) {
        console.warn(`⚠️ Could not fetch preference for user ${userId}, defaulting to 'available':`, prefError.message);
        // Continue with default - don't fail the entire analysis for preference fetch errors
      }

      if (userPreferenceType === 'unavailable') {
        // Convert busy times to available times for scheduling algorithm
        const convertedAvailability = {};
        for (const [date, timeRanges] of Object.entries(userData.availability)) {
          if (timeRanges && timeRanges.length > 0) {
            convertedAvailability[date] = convertBusyTimesToAvailable(timeRanges);
          }
        }

        processedAvailabilityData[userId] = {
          ...userData,
          availability: convertedAvailability
        };
      } else {
        // User has 'available' preference type - use data as-is
        processedAvailabilityData[userId] = userData;
      }
    }
  }
  
  // Check if any members have actual availability data after processing
  const membersWithData = Object.entries(processedAvailabilityData).filter(([userId, userData]) => {
    return userData.availability && Object.keys(userData.availability).length > 0;
  });

  if (membersWithData.length === 0) {
    return { data: [], error: new Error('No members have availability data. Please ask members to set their availability in their profiles.') };
  }

  const processingTime = Date.now() - processingStartTime;

  // Extract and filter dates from processed availability data
  const availableDates = new Set();
  Object.entries(processedAvailabilityData).forEach(([userId, userData]) => {
    if (userData.availability) {
      Object.keys(userData.availability).forEach(date => {
        availableDates.add(date);
      });
    }
  });

  const actualAvailabilityDates = Array.from(availableDates).sort();
  const requestedDates = generateDatesInRange(dateRange.startDate, dateRange.endDate);
  const availabilityDatesInRange = filterAvailabilityDatesToRange(
    actualAvailabilityDates,
    dateRange.startDate,
    dateRange.endDate
  );

  // Use intersection of requested dates and available dates
  let datesToAnalyze = requestedDates.filter(date => 
    availabilityDatesInRange.includes(date)
  );

  // If no intersection, still analyze the requested dates
  if (datesToAnalyze.length === 0) {
    datesToAnalyze = requestedDates;
  }

  const timeWindows = generateTimeWindows(holeCount);
  const results = [];

  // Analyze each date and time window combination
  const slotAnalysisStartTime = Date.now();
  
  
  for (const date of datesToAnalyze) {
    // SAFETY CHECK: Ensure date is within range
    const isDateInRange = requestedDates.includes(date);
    if (!isDateInRange) {
      continue;
    }

    for (const window of timeWindows) {
      const analysis = analyseTimeSlot(date, window, processedAvailabilityData, selectedMemberIds, holeCount);
      
      // For group scheduling: require 2+ members
      // For solo scheduling (1 selected member): require 1 member
      // AND ensure current user is available (if currentUserId is provided)
      const currentUserIsAvailable = !currentUserId || analysis.availableMembers.some(member => member.userId === currentUserId);
      const isSoloScheduling = selectedMemberIds.length === 1;
      const minimumMembers = isSoloScheduling ? 1 : 2;

      if (analysis.availableMembers.length >= minimumMembers && currentUserIsAvailable) {
        if (import.meta.env.DEV) {
          console.log('Adding valid slot:', { date, window: window.start, availableMembers: analysis.availableMembers.length });
        }
        results.push({
          date,
          window,
          holeCount, // needed by daylight scoring + filter to use the right round duration
          ...analysis
        });
      } else {
        if (import.meta.env.DEV) {
          console.log('Rejecting slot:', { 
            date, 
            window: window.start, 
            availableMembers: analysis.availableMembers.length,
            currentUserAvailable: currentUserIsAvailable
          });
        }
      }
    }
  }

  // Filter results to ensure only slots within date range
  const slotAnalysisTime = Date.now() - slotAnalysisStartTime;
  
  if (import.meta.env.DEV) {
    console.log('Results before filtering:', results.length);
  }
  const filteredResults = filterSlotsToDateRange(results, dateRange.startDate, dateRange.endDate);
  if (import.meta.env.DEV) {
    console.log('Results after filtering:', filteredResults.length);
  }

  // ENHANCED: Add weather data if golf course coordinates are available
  const weatherStartTime = Date.now();
  let resultsWithWeather = filteredResults;

  // Extract golf club info early so it's available in catch block
  let golfClubName = '';
  let isCustomCourse = false;
  let courseCoordinates = null;

  if (golfClub) {
    if (typeof golfClub === 'string') {
      golfClubName = golfClub.trim();
    } else if (typeof golfClub === 'object' && golfClub.name) {
      golfClubName = golfClub.name.trim();
      isCustomCourse = golfClub.is_custom || false;
    }
  }

  // Get coordinates early (outside try block) so they're available for daylight scoring
  // even if weather API fails
  if (golfClubName && golfClubName !== 'To be determined') {
    try {
      courseCoordinates = await getGolfCourseCoordinates(golfClubName);
    } catch (coordError) {
      console.warn('Could not get golf course coordinates:', coordError.message);
    }
  }

  try {
    if (golfClubName && golfClubName !== 'To be determined' && courseCoordinates) {

      const coordinates = courseCoordinates;
      
      if (coordinates && isWeatherAvailable(coordinates.lat, coordinates.lon)) {
        
        // Prepare time slots for weather analysis. Build a canonical UTC
        // instant per slot using the course's IANA tz so the browser picker
        // and the Deno-side weather refresh agree on the forecast hour.
        const timeSlots = filteredResults.map(result => {
          const instant = roundStartInstant({
            date: result.date,
            time: result.window.start,
            course_latitude: coordinates.lat,
            course_longitude: coordinates.lon,
          });
          const dateTimeString = instant
            ? instant.toISOString()
            : `${result.date}T${result.window.start}:00`;
          console.log('Creating time slot:', {
            date: result.date,
            window: result.window.start,
            dateTimeString: dateTimeString
          });
          return {
            ...result,
            startTime: dateTimeString,
            datetime: dateTimeString
          };
        });
        
        // Fetch weather data for all time slots
        const weatherEnhancedSlots = await getWeatherForTimeSlots(
          coordinates.lat, 
          coordinates.lon, 
          timeSlots
        );
        
        // Merge weather data back into results
        resultsWithWeather = weatherEnhancedSlots.map(slot => ({
          ...slot,
          weather: slot.weather,
          weatherScore: slot.weatherScore || 0,
          weatherError: slot.weatherError,
          golfCourse: {
            name: golfClubName,
            coordinates: coordinates,
            hasWeatherData: !!slot.weather && !slot.weatherError,
            is_custom: isCustomCourse
          }
        }));
        
        const weatherTime = Date.now() - weatherStartTime;
      } else {
        // Weather not available for this location, but preserve coordinates for daylight scoring
        resultsWithWeather = filteredResults.map(result => ({
          ...result,
          weather: null,
          weatherScore: 0,
          golfCourse: {
            name: golfClubName,
            coordinates: courseCoordinates, // Preserve coordinates for daylight scoring
            hasWeatherData: false,
            is_custom: isCustomCourse
          }
        }));
      }
    } else if (golfClubName && golfClubName !== 'To be determined') {
      // Golf club specified but no coordinates found
      resultsWithWeather = filteredResults.map(result => ({
        ...result,
        weather: null,
        weatherScore: 0,
        golfCourse: {
          name: golfClubName,
          coordinates: null,
          hasWeatherData: false,
          is_custom: isCustomCourse
        }
      }));
    } else {
      // No golf course specified, add null weather data
      resultsWithWeather = filteredResults.map(result => ({
        ...result,
        weather: null,
        weatherScore: 0,
        golfCourse: null
      }));
    }
  } catch (weatherError) {
    const weatherTime = Date.now() - weatherStartTime;
    console.warn(`⚠️ Weather integration failed after ${weatherTime}ms, continuing without weather data:`, weatherError.message);

    // Graceful degradation: continue with scheduling without weather data
    // This ensures the main functionality still works even if weather API fails
    // IMPORTANT: Preserve courseCoordinates so daylight scoring can still work
    resultsWithWeather = filteredResults.map(result => ({
      ...result,
      weather: null,
      weatherScore: 0,
      weatherError: `Weather data unavailable: ${weatherError.message}`,
      golfCourse: golfClubName ? {
        name: golfClubName,
        coordinates: courseCoordinates, // Preserve coordinates for daylight scoring
        hasWeatherData: false,
        is_custom: isCustomCourse,
        error: weatherError.message
      } : null
    }));

  }

  // Filter out slots with unplayable weather conditions
  const preFilterCount = resultsWithWeather.length;
  resultsWithWeather = resultsWithWeather.filter(slot => {
    if (slot.weather?.isUnplayable) {
      console.log(`[Weather] Filtering out unplayable slot: ${slot.date} ${slot.window?.start} - ${slot.weather.unplayableReason}`);
      return false;
    }
    return true;
  });
  const postFilterCount = resultsWithWeather.length;
  if (preFilterCount !== postFilterCount) {
    console.log(`[Weather] Filtered out ${preFilterCount - postFilterCount} unplayable slots (${postFilterCount} remaining)`);
  }

  // Safety net: drop slots that fall (almost) entirely outside daylight. Now the
  // full 24h day is searched, the daylight score alone (weighted 0.2) cannot
  // always bury a dead-of-night slot when daytime options are scarce, so we
  // remove genuinely dark rounds outright. Marginal dawn/dusk slots survive and
  // are ranked by the composite score.
  const preDaylightCount = resultsWithWeather.length;
  resultsWithWeather = resultsWithWeather.filter(slot => {
    const coords = slot.golfCourse?.coordinates;
    // Can't assess daylight (no coordinates / missing inputs) -> keep the slot.
    if (!coords?.lat || !coords?.lon || !slot.date || !slot.window?.start) {
      return true;
    }
    const durationHours = slot.holeCount === 9 ? 2 : 4;
    let daylight;
    try {
      daylight = calculateDaylightScore(slot.date, slot.window.start, durationHours, coords.lat, coords.lon);
    } catch (error) {
      return true; // assessment failed -> keep
    }
    // overlapFraction null = sun data unavailable (e.g. polar day/night) -> keep,
    // don't guess. Otherwise drop when the round is effectively un-daylit.
    if (daylight?.overlapFraction == null) {
      return true;
    }
    if (daylight.overlapFraction <= DAYLIGHT_CONFIG.minPlayableOverlapFraction) {
      console.log(`[Daylight] Filtering out dark slot: ${slot.date} ${slot.window.start} (overlap ${(daylight.overlapFraction * 100).toFixed(0)}%)`);
      return false;
    }
    // Tee-off gate: at high summer latitudes a dead-of-night start can still
    // clear the overlap filter (long twilight, 4h round reaching daylight), so
    // drop any slot teeing off well before sunrise or after sunset outright.
    if (daylight.teeOffBeforeSunriseMin != null && daylight.teeOffBeforeSunriseMin > DAYLIGHT_CONFIG.maxTeeOffBeforeSunriseMin) {
      console.log(`[Daylight] Filtering out pre-dawn tee-off: ${slot.date} ${slot.window.start} (${daylight.teeOffBeforeSunriseMin} min before sunrise)`);
      return false;
    }
    if (daylight.teeOffAfterSunsetMin != null && daylight.teeOffAfterSunsetMin > 0) {
      console.log(`[Daylight] Filtering out post-sunset tee-off: ${slot.date} ${slot.window.start} (${daylight.teeOffAfterSunsetMin} min after sunset)`);
      return false;
    }
    return true;
  });
  const postDaylightCount = resultsWithWeather.length;
  if (preDaylightCount !== postDaylightCount) {
    console.log(`[Daylight] Filtered out ${preDaylightCount - postDaylightCount} dark slots (${postDaylightCount} remaining)`);
  }

  // ENHANCED: Sort by composite score including weather (if available) and member count
  const sortedResults = resultsWithWeather
    .map(result => ({
      ...result,
      compositeScore: calculateCompositeScore(result)
    }))
    .sort((a, b) => b.compositeScore - a.compositeScore);

  // FINAL SAFETY CHECK: Verify all results are within range
  const invalidResults = sortedResults.filter(result => 
    !requestedDates.includes(result.date)
  );

  if (invalidResults.length > 0) {
    // Remove any invalid results as a final safety measure
    const finalValidResults = sortedResults.filter(result =>
      requestedDates.includes(result.date)
    );
    const totalAnalysisTime = Date.now() - analysisStartTime;
    return { data: finalValidResults, error: null, availabilityData: processedAvailabilityData };
  }

  const totalAnalysisTime = Date.now() - analysisStartTime;

  if (sortedResults.length > 0) {
  }

  return { data: sortedResults, error: null, availabilityData: processedAvailabilityData };
};

// Calculate composite score combining member availability, weather, and daylight
// Scoring System v2.0 - Uses smooth mathematical curves for nuanced rankings
export const calculateCompositeScore = (result) => {
  // Solo flow has no group, so no availableMembers array - the member
  // component is skipped and the score weights on weather + daylight only.
  const isSolo = !Array.isArray(result.availableMembers);

  // Member availability score using non-linear power curve (0-10 scale).
  // Curve exponent < 1 lifts the middle (50% attendance ≈ 6.6 instead of 5.0).
  const maxPossibleMembers = result.totalMembers || 1;
  const memberScore = isSolo
    ? null
    : (maxPossibleMembers === 0
        ? 0
        : Math.pow(result.availableMembers.length / maxPossibleMembers, MEMBER_CURVE.exponent) * 10);

  // Weather score (0-10 scale, from weather service)
  const weatherScore = result.weatherScore || 0;

  // Calculate daylight score INDEPENDENTLY (using coordinates, not weather API)
  let daylightScore = null;
  let daylightCategory = 'unknown';
  const coords = result.golfCourse?.coordinates;

  if (coords?.lat && coords?.lon && result.date && result.window?.start) {
    // Determine round duration: 2 hours for 9 holes, 4 hours for 18 holes
    const durationHours = result.holeCount === 9 ? 2 : 4;
    try {
      const daylightResult = calculateDaylightScore(
        result.date,
        result.window.start,
        durationHours,
        coords.lat,
        coords.lon
      );
      if (daylightResult) {
        daylightScore = daylightResult.score;
        daylightCategory = daylightResult.category;
      }
    } catch (error) {
      console.warn('Error calculating daylight score:', error);
    }
  }

  // Store daylight info on result for UI to use
  result.daylightScore = daylightScore;
  result.daylightCategory = daylightCategory;

  // Apply weighted scoring based on available data.
  let compositeScore;
  const hasWeather = result.weather && result.golfCourse?.hasWeatherData;
  const hasDaylight = daylightScore !== null;

  if (isSolo) {
    // Solo flow: redistribute member weight across the remaining components.
    if (hasWeather && hasDaylight) {
      // Drop member, keep the relative 0.4 / 0.2 split → 2/3 weather, 1/3 daylight.
      compositeScore = (weatherScore * 2 / 3) + (daylightScore * 1 / 3);
    } else if (hasDaylight) {
      compositeScore = daylightScore;
    } else if (hasWeather) {
      compositeScore = weatherScore;
    } else {
      // No member, no weather, no daylight - nothing to score on. Park at
      // mid-scale rather than 0 so the slot isn't visually penalised.
      compositeScore = 5;
    }
  } else if (hasWeather && hasDaylight) {
    const w = COMPOSITE_WEIGHTS.full;
    compositeScore = (memberScore * w.member) + (weatherScore * w.weather) + (daylightScore * w.daylight);
  } else if (hasDaylight) {
    const w = COMPOSITE_WEIGHTS.noWeather;
    compositeScore = (memberScore * w.member) + (daylightScore * w.daylight);
  } else if (hasWeather) {
    const w = COMPOSITE_WEIGHTS.noDaylight;
    compositeScore = (memberScore * w.member) + (weatherScore * w.weather);
  } else {
    compositeScore = memberScore;
  }

  const finalScore = Math.round(compositeScore * 10) / 10;
  return Math.min(COMPOSITE_SCORE_CAP, finalScore);
};

// Analyse a specific time slot with hole count consideration - exported for reuse in golfNowService.js
export const analyseTimeSlot = (date, window, availabilityData, selectedMemberIds, holeCount = 18) => {
  const availableMembers = [];
  const unavailableMembers = [];
  
  // Determine minimum overlap based on hole count
  const minimumOverlapMinutes = holeCount === 9 ? 120 : 240; // 2 hours for 9 holes, 4 hours for 18 holes

  Object.entries(availabilityData).forEach(([userId, userData]) => {
    const userAvailability = userData.availability[date] || [];
    let userTotalOverlap = 0;

    if (import.meta.env.DEV) {
      console.log(`Checking user ${userId} for date ${date}:`, {
        userAvailability: userAvailability,
        window: window,
        minimumOverlapMinutes: minimumOverlapMinutes
      });
    }

    // Check if user has any availability that overlaps with this window
    const hasOverlap = userAvailability.some(userRange => {
      if (timeRangesOverlap(window, userRange)) {
        const overlapDuration = calculateOverlapDuration(window, userRange);
        userTotalOverlap += overlapDuration;
        if (import.meta.env.DEV) {
          console.log(`  - Overlap found with range ${userRange.start}-${userRange.end}: ${overlapDuration} minutes`);
        }
        return true;
      }
      return false;
    });

    if (import.meta.env.DEV) {
      console.log(`  - Total overlap: ${userTotalOverlap} minutes (needs ${minimumOverlapMinutes})`);
    }

    if (hasOverlap && userTotalOverlap >= minimumOverlapMinutes) {
      if (import.meta.env.DEV) {
        console.log(`  - ✅ User ${userId} is AVAILABLE`);
      }
      availableMembers.push({
        userId,
        userInfo: userData.user,
        overlapMinutes: userTotalOverlap
      });
    } else {
      if (import.meta.env.DEV) {
        console.log(`  - ❌ User ${userId} is NOT AVAILABLE (overlap: ${userTotalOverlap}, hasOverlap: ${hasOverlap})`);
      }
      unavailableMembers.push({
        userId,
        userInfo: userData.user
      });
    }
  });

  return {
    availableMembers,
    unavailableMembers,
    totalMembers: availableMembers.length + unavailableMembers.length
  };
};

// Find available members for a specific time slot
export const findAvailableMembers = async (groupId, date, timeWindow) => {
  try {
    const memberIds = await getAllGroupMemberIds(groupId);
    const { data: availabilityData, error } = await getGroupMembersAvailability(
      memberIds,
      date,
      date,
      groupId
    );

    if (error) throw error;

    // Process availability data to handle preference types
    const processedAvailabilityData = {};
    for (const [userId, userData] of Object.entries(availabilityData)) {
      const userPreferenceType = await getUserPreferenceType(userId) || 'available';
      
      if (userPreferenceType === 'unavailable' && userData.availability && userData.availability[date]) {
        const convertedRanges = convertBusyTimesToAvailable(userData.availability[date]);
        processedAvailabilityData[userId] = {
          ...userData,
          availability: {
            [date]: convertedRanges
          }
        };
      } else {
        processedAvailabilityData[userId] = userData;
      }
    }

    const analysis = analyseTimeSlot(date, timeWindow, processedAvailabilityData, memberIds);
    return { data: analysis, error: null };
  } catch (error) {
    console.error('Error in findAvailableMembers:', error);
    return { data: null, error };
  }
};

// UPDATED: Create a smart round based on optimal time slot with hole count
export const createSmartRound = async (roundData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Determine member IDs - require explicit selection (no fallback to all members)
    const selectedMemberIds = roundData.selectedMemberIds || roundData.selectedMembers;
    if (!selectedMemberIds || selectedMemberIds.length === 0) {
      throw new Error('No members selected for round. Please select at least 2 members.');
    }
    const validMemberIds = selectedMemberIds.filter(id => id && typeof id === 'string');

    // Validate: Group rounds require at least 2 participants
    if (roundData.groupId && validMemberIds.length < 2) {
      throw new Error('Group rounds require at least 2 participants');
    }

    // Create round
    const { data: round, error: roundError } = await supabase
      .from('rounds')
      .insert([{
        group_id: roundData.groupId || null,
        date: roundData.date,
        time: roundData.timeWindow.start,
        course: resolveRoundCourseString(roundData.golfClub) || resolveRoundCourseString(roundData.course) || null,
        hole_count: roundData.holeCount || 18,
        created_by: user.id,
        round_type: roundData.roundType || (roundData.groupId ? 'group' : 'solo'),
        ...resolveRoundInsertTzFields(roundData),
      }])
      .select()
      .single();

    if (roundError) {
      console.error('Error creating smart round:', roundError);
      throw roundError;
    }

    // Create invitations for validated members (validMemberIds determined above)
    if (validMemberIds.length > 0) {
      const invitations = validMemberIds.map(userId => ({
        round_id: round.id,
        user_id: userId,
        status: 'pending',
        invited_at: new Date().toISOString()
      }));

      try {
        const { error: invitationError } = await supabase
          .from('round_invitations')
          .upsert(invitations, {
            onConflict: 'round_id,user_id',
            ignoreDuplicates: true
          });

        if (invitationError) {
          // Check if it's a duplicate key error (409)
          if (invitationError.code !== '23505' && invitationError.status !== 409) {
            console.error('Error creating invitations:', invitationError);
          }
        }
      } catch (error) {
        console.error('Exception creating invitations:', error);
        // Don't throw - round was created successfully
      }
    } else {
      console.warn('No valid member IDs found for creating invitations');
    }

    // Auto-accept the creator and sync to Outlook calendar
    try {
      console.log('🎯 Auto-accepting creator for round:', round.id);
      const { error: acceptError } = await supabase
        .from('round_invitations')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString()
        })
        .eq('round_id', round.id)
        .eq('user_id', user.id);

      if (acceptError) {
        console.error('❌ Error auto-accepting creator:', acceptError);
      } else {
        console.log('✅ Creator auto-accepted, attempting calendar sync');
        await autoSyncRoundToCalendar(round.id);
      }
    } catch (error) {
      console.error('⚠️  Error auto-accepting creator or syncing calendar:', error);
      // Don't throw - round was created successfully
    }

    // Notifications (in-app, email, push) are now handled server-side via database trigger
    // The round_invitations insert triggers process-notification edge function
    console.log('📬 Notifications will be sent server-side via database trigger for round:', round.id);

    // Fetch and store weather data for the round (non-blocking)
    try {
      const courseName = roundData.golfClub || roundData.course || null;
      await fetchAndStoreRoundWeather(round.id, courseName, roundData.date, roundData.timeWindow.start, roundData.holeCount || 18);
    } catch (weatherError) {
      console.warn('⚠️ Weather fetch failed (non-critical):', weatherError);
      // Don't throw - round was created successfully
    }

    // Cache course website and phone for booking feature (non-blocking)
    try {
      const courseName = roundData.golfClub || roundData.course || null;
      if (courseName) {
        const { website, googleMapsUri, phone } = await getCourseWebsite(courseName);
        if (website || googleMapsUri || phone) {
          await supabase
            .from('rounds')
            .update({
              course_website: website || googleMapsUri,
              course_phone: phone
            })
            .eq('id', round.id);
        }
      }
    } catch (websiteError) {
      console.warn('⚠️ Failed to cache course website (non-critical):', websiteError);
      // Don't throw - round was created successfully
    }

    // Cache GolfNow booking URL if provided (from GolfNow tee time selection)
    try {
      if (roundData.golfnowBookingUrl) {
        await supabase
          .from('rounds')
          .update({ golfnow_booking_url: roundData.golfnowBookingUrl })
          .eq('id', round.id);
      }
    } catch (golfnowError) {
      console.warn('⚠️ Failed to cache GolfNow booking URL (non-critical):', golfnowError);
    }

    if (roundData.openInvite?.isOpenInvite) {
      await applyOpenInviteSettings(round.id, roundData.openInvite);
    }

    recordPositiveAction('round_scheduled');
    return { data: round, error: null };
  } catch (error) {
    console.error('Error creating smart round:', error);
    return { data: null, error };
  }
};

// Create a Smart round with multiple proposed tee-time slots instead of one
// committed time. Reuses createSmartRound for the round + invitations +
// weather plumbing (rounds.date/time are NOT NULL, so the earliest slot is
// stored as a placeholder), then flips scheduling_status to 'proposed' and
// records the candidate slots. The organiser later picks one via
// confirmRoundTime().
export const createProposedRound = async (roundData, slots) => {
  if (!slots || slots.length < 2 || slots.length > 6) {
    return { data: null, error: new Error('Propose between 2 and 6 time slots') };
  }

  const sorted = [...slots].sort((a, b) =>
    `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`)
  );
  const earliest = sorted[0];

  const result = await createSmartRound({
    ...roundData,
    date: earliest.date,
    timeWindow: { start: earliest.time },
  });
  if (result.error || !result.data) return result;

  const roundId = result.data.id;
  try {
    const { error: statusError } = await supabase
      .from('rounds')
      .update({ scheduling_status: 'proposed' })
      .eq('id', roundId);
    if (statusError) throw statusError;

    const { error: slotsError } = await supabase
      .from('round_proposed_slots')
      .insert(sorted.map(s => ({
        round_id: roundId,
        slot_date: s.date,
        slot_time: s.time,
      })));
    if (slotsError) throw slotsError;

    return { data: { ...result.data, scheduling_status: 'proposed' }, error: null };
  } catch (error) {
    console.error('Error recording proposed slots:', error);
    return { data: result.data, error };
  }
};

// Organiser confirms one proposed slot - sets rounds.date/time, flips
// scheduling_status, and fans out notifications (members in-app/push,
// guests by email via edge function) server-side.
export const confirmRoundTime = async (roundId, slotId) => {
  try {
    const { error } = await supabase.rpc('confirm_round_time', {
      p_round_id: roundId,
      p_slot_id: slotId,
    });
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error confirming round time:', error);
    return { error };
  }
};

// Toggle the public guest link for a round (creator only via RLS)
export const setLinkSharingEnabled = async (roundId, enabled) => {
  try {
    const { error } = await supabase
      .from('rounds')
      .update({ link_sharing_enabled: enabled })
      .eq('id', roundId);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error updating link sharing:', error);
    return { error };
  }
};

// UPDATED: Create a manual round with hole count
export const createManualRound = async (roundData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Extract date and time from datetime field
    let date, time;
    if (roundData.datetime) {
      const dateTimeObj = new Date(roundData.datetime);
      const year = dateTimeObj.getFullYear();
      const month = String(dateTimeObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateTimeObj.getDate()).padStart(2, '0');
      date = `${year}-${month}-${day}`; // YYYY-MM-DD in local time
      time = dateTimeObj.toTimeString().slice(0, 5); // HH:MM
    } else {
      // Fallback to separate fields if they exist
      date = roundData.date;
      time = roundData.time;
    }

    // Validate that we have valid date and time
    if (!date || !time) {
      throw new Error('Invalid date or time provided');
    }

    const courseName = resolveRoundCourseString(roundData.golfClub) ||
                      resolveRoundCourseString(roundData.golfCourse) ||
                      resolveRoundCourseString(roundData.course) ||
                      null;

    // Determine member IDs - require explicit selection (no fallback to all members)
    const selectedMemberIds = roundData.selectedMemberIds || roundData.selectedMembers;
    if (!selectedMemberIds || selectedMemberIds.length === 0) {
      throw new Error('No members selected for round. Please select at least 2 members.');
    }
    const validMemberIds = selectedMemberIds.filter(id => id && typeof id === 'string');

    // Validate: Group rounds require at least 2 participants
    if (roundData.groupId && validMemberIds.length < 2) {
      throw new Error('Group rounds require at least 2 participants');
    }

    // Create round
    const { data: round, error: roundError } = await supabase
      .from('rounds')
      .insert([{
        group_id: roundData.groupId || null,
        date: date,
        time: time,
        course: courseName,
        hole_count: roundData.holeCount || 18,
        created_by: user.id,
        round_type: roundData.roundType || (roundData.groupId ? 'group' : 'solo'),
        ...resolveRoundInsertTzFields(roundData),
      }])
      .select()
      .single();

    if (roundError) {
      console.error('Error creating manual round:', roundError);
      throw roundError;
    }

    // Create invitations for validated members (validMemberIds determined above)
    if (validMemberIds.length > 0) {
      const invitations = validMemberIds.map(userId => ({
        round_id: round.id,
        user_id: userId,
        status: 'pending',
        invited_at: new Date().toISOString()
      }));

      try {
        const { error: invitationError } = await supabase
          .from('round_invitations')
          .upsert(invitations, {
            onConflict: 'round_id,user_id',
            ignoreDuplicates: true
          });

        if (invitationError) {
          // Check if it's a duplicate key error (409)
          if (invitationError.code !== '23505' && invitationError.status !== 409) {
            console.error('Error creating invitations:', invitationError);
          }
        }
      } catch (error) {
        console.error('Exception creating invitations:', error);
        // Don't throw - round was created successfully
      }
    } else {
      console.warn('No valid member IDs found for creating invitations');
    }

    // Auto-accept the creator and sync to Outlook calendar
    try {
      console.log('🎯 Auto-accepting creator for round:', round.id);
      const { error: acceptError } = await supabase
        .from('round_invitations')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString()
        })
        .eq('round_id', round.id)
        .eq('user_id', user.id);

      if (acceptError) {
        console.error('❌ Error auto-accepting creator:', acceptError);
      } else {
        console.log('✅ Creator auto-accepted, attempting calendar sync');
        await autoSyncRoundToCalendar(round.id);
      }
    } catch (error) {
      console.error('⚠️  Error auto-accepting creator or syncing calendar:', error);
      // Don't throw - round was created successfully
    }

    // Notifications (in-app, email, push) are now handled server-side via database trigger
    // The round_invitations insert triggers process-notification edge function
    console.log('📬 Notifications will be sent server-side via database trigger for round:', round.id);

    // Fetch and store weather data for the round (non-blocking)
    try {
      await fetchAndStoreRoundWeather(round.id, courseName, date, time, roundData.holeCount || 18);
    } catch (weatherError) {
      console.warn('⚠️ Weather fetch failed (non-critical):', weatherError);
      // Don't throw - round was created successfully
    }

    // Cache course website and phone for booking feature (non-blocking)
    try {
      if (courseName) {
        const { website, googleMapsUri, phone } = await getCourseWebsite(courseName);
        if (website || googleMapsUri || phone) {
          await supabase
            .from('rounds')
            .update({
              course_website: website || googleMapsUri,
              course_phone: phone
            })
            .eq('id', round.id);
        }
      }
    } catch (websiteError) {
      console.warn('⚠️ Failed to cache course website (non-critical):', websiteError);
      // Don't throw - round was created successfully
    }

    if (roundData.openInvite?.isOpenInvite) {
      await applyOpenInviteSettings(round.id, roundData.openInvite);
    }

    recordPositiveAction('round_scheduled');
    return { data: round, error: null };
  } catch (error) {
    console.error('Error creating manual round:', error);
    return { data: null, error };
  }
};

/**
 * Invite one or more users to an existing round.
 *
 * Upserts pending rows into `round_invitations` using the `(round_id, user_id)`
 * unique constraint as idempotency. Already-invited users are silently
 * absorbed (ignoreDuplicates), so retrying the same payload is safe. The
 * `on_round_invitation_created` DB trigger fires the notification email/push.
 *
 * @param {number} roundId
 * @param {string[]} userIds
 * @returns {Promise<{data: any, error: Error|null}>}
 */
export const inviteUsersToRound = async (roundId, userIds) => {
  try {
    if (!roundId) throw new Error('roundId is required');
    const cleanIds = (Array.isArray(userIds) ? userIds : [])
      .filter((id) => typeof id === 'string' && id.length > 0);
    if (cleanIds.length === 0) {
      return { data: [], error: null };
    }

    const now = new Date().toISOString();
    const rows = cleanIds.map((userId) => ({
      round_id: roundId,
      user_id: userId,
      status: 'pending',
      invited_at: now,
    }));

    const { data, error } = await supabase
      .from('round_invitations')
      .upsert(rows, { onConflict: 'round_id,user_id', ignoreDuplicates: true })
      .select();

    if (error) {
      console.error('Error inviting users to round:', error);
      return { data: null, error };
    }
    return { data: data || [], error: null };
  } catch (err) {
    console.error('Exception in inviteUsersToRound:', err);
    return { data: null, error: err };
  }
};

// Create a solo round (no group association)
export const createSoloRound = async (roundData, currentUser = null) => {
  try {
    // Support both Supabase Auth and test users
    let user = currentUser;
    if (!user) {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      user = authUser;
    }
    if (!user) throw new Error('User not authenticated');

    // Extract date and time from datetime field or use separate fields
    let date, time;
    if (roundData.datetime) {
      const dateTimeObj = new Date(roundData.datetime);
      const year = dateTimeObj.getFullYear();
      const month = String(dateTimeObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateTimeObj.getDate()).padStart(2, '0');
      date = `${year}-${month}-${day}`; // YYYY-MM-DD in local time
      time = dateTimeObj.toTimeString().slice(0, 5); // HH:MM
    } else {
      date = roundData.date;
      time = roundData.time;
    }

    // Validate that we have valid date and time
    if (!date || !time) {
      throw new Error('Invalid date or time provided');
    }

    const courseName = resolveRoundCourseString(roundData.golfClub) ||
                      resolveRoundCourseString(roundData.golfCourse) ||
                      resolveRoundCourseString(roundData.course) ||
                      null;

    console.log('🎯 Creating solo round:', { date, time, courseName, holeCount: roundData.holeCount });

    // Create round with group_id = null (solo round)
    const { data: round, error: roundError } = await supabase
      .from('rounds')
      .insert([{
        group_id: null,  // Solo round - no group association
        date: date,
        time: time,
        course: courseName,
        hole_count: roundData.holeCount || 18,
        notes: roundData.notes || null,
        created_by: user.id,
        course_phone: roundData.coursePhone || null,
        course_website: roundData.courseWebsite || null,
        golfnow_booking_url: roundData.golfnowBookingUrl || null,
        ...resolveRoundInsertTzFields(roundData),
      }])
      .select()
      .single();

    if (roundError) {
      console.error('Error creating solo round:', roundError);
      throw roundError;
    }

    console.log('✅ Solo round created:', round.id);

    // Create invitation for the creator (auto-accepted)
    try {
      const { error: invitationError } = await supabase
        .from('round_invitations')
        .insert([{
          round_id: round.id,
          user_id: user.id,
          status: 'accepted',  // Auto-accept for solo rounds
          invited_at: new Date().toISOString(),
          responded_at: new Date().toISOString()
        }]);

      if (invitationError) {
        console.error('Error creating solo round invitation:', invitationError);
      } else {
        console.log('✅ Creator invitation created and auto-accepted');
      }
    } catch (error) {
      console.error('Exception creating solo round invitation:', error);
      // Don't throw - round was created successfully
    }

    // Sync to Outlook calendar if connected
    try {
      console.log('📅 Attempting to sync solo round to Outlook calendar');
      await autoSyncRoundToCalendar(round.id);
    } catch (error) {
      console.error('⚠️ Error syncing solo round to calendar:', error);
      // Don't throw - round was created successfully
    }

    // Fetch and store weather data for the round (non-blocking)
    try {
      await fetchAndStoreRoundWeather(round.id, courseName, date, time, roundData.holeCount || 18);
    } catch (weatherError) {
      console.warn('⚠️ Weather fetch failed (non-critical):', weatherError);
      // Don't throw - round was created successfully
    }

    // Cache course website and phone for booking feature (non-blocking)
    try {
      if (courseName) {
        const { website, googleMapsUri, phone } = await getCourseWebsite(courseName);
        if (website || googleMapsUri || phone) {
          await supabase
            .from('rounds')
            .update({
              course_website: website || googleMapsUri,
              course_phone: phone
            })
            .eq('id', round.id);
        }
      }
    } catch (websiteError) {
      console.warn('⚠️ Failed to cache course website (non-critical):', websiteError);
      // Don't throw - round was created successfully
    }

    if (roundData.openInvite?.isOpenInvite) {
      await applyOpenInviteSettings(round.id, roundData.openInvite);
    }

    recordPositiveAction('round_scheduled');
    return { data: round, error: null };
  } catch (error) {
    console.error('Error creating solo round:', error);
    return { data: null, error };
  }
};

// Get rounds for a group
export const getGroupRounds = async (groupId) => {
  try {
    const { data: rounds, error } = await supabase
      .from('rounds')
      .select(`
        *,
        round_invitations (
          user_id,
          status,
          responded_at,
          guest_name
        )
      `)
      .eq('group_id', groupId)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching group rounds:', error);
      throw error;
    }

    // Enrich round_invitations with member names (guests have no user_id)
    const allUserIds = [...new Set(
      rounds?.flatMap(r => r.round_invitations?.map(inv => inv.user_id) || []) || []
    )].filter(Boolean);
    if (allUserIds.length > 0) {
      const { data: members } = await supabase
        .from('group_members')
        .select('user_id, member_name')
        .eq('group_id', groupId)
        .in('user_id', allUserIds);

      const memberMap = {};
      members?.forEach(m => { memberMap[m.user_id] = m.member_name; });

      rounds?.forEach(round => {
        round.round_invitations?.forEach(inv => {
          inv.member_name = inv.user_id ? (memberMap[inv.user_id] || null) : (inv.guest_name || 'Guest');
        });
      });
    }

    return { data: rounds, error: null };
  } catch (error) {
    console.error('Error fetching group rounds:', error);
    return { data: [], error };
  }
};

// Fetch a round by its short_code (used for shareable round URLs)
export const getRoundByShortCode = async (shortCode) => {
  try {
    const { data, error } = await supabase.rpc('get_round_by_short_code', {
      p_short_code: shortCode
    });
    if (error) {
      console.error('Error fetching round by short code:', error);
      return { data: null, error };
    }
    return { data: data?.[0] || null, error: null };
  } catch (error) {
    console.error('Exception fetching round by short code:', error);
    return { data: null, error };
  }
};

// Get rounds with user invitation status AND group names
export const getRoundsWithInvitationStatus = async (groupId = null, currentUser = null) => {
  try {
    // Support both Supabase Auth and test users
    let user = currentUser;
    if (!user) {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      user = authUser;
    }
    if (!user) throw new Error('User not authenticated');

    console.log('📋 Fetching rounds for user:', user.id);

    // First, get all round IDs where the user has an invitation
    const { data: userInvitations, error: invError } = await supabase
      .from('round_invitations')
      .select('round_id, status, responded_at, invited_at')
      .eq('user_id', user.id);

    if (invError) {
      console.error('Error fetching user invitations:', invError);
      throw invError;
    }

    console.log('📋 User has invitations for', userInvitations?.length || 0, 'rounds');

    // If user has no invitations, return empty array
    if (!userInvitations || userInvitations.length === 0) {
      return { data: [], error: null };
    }

    // Get the round IDs the user is invited to
    const roundIds = userInvitations.map(inv => inv.round_id);

    // Build query to fetch rounds the user is invited to
    let query = supabase
      .from('rounds')
      .select(`
        *,
        golf_groups!rounds_group_id_fkey (
          id,
          name,
          invite_code
        ),
        round_invitations!left (
          user_id,
          status,
          responded_at,
          invited_at
        )
      `)
      .in('id', roundIds)
      .order('date', { ascending: true });

    // Filter by group if groupId provided
    if (groupId) {
      query = query.eq('group_id', groupId);
    }

    const { data: rounds, error } = await query;

    if (error) {
      console.error('Error fetching rounds with invitation status:', error);
      throw error;
    }

    console.log('📋 Fetched', rounds?.length || 0, 'rounds');

    // Enrich round_invitations with member names
    const allUserIds = [...new Set(rounds?.flatMap(r => r.round_invitations?.map(inv => inv.user_id) || []) || [])];
    const groupIds = [...new Set(rounds?.map(r => r.group_id).filter(Boolean) || [])];

    let memberMap = {};
    if (allUserIds.length > 0 && groupIds.length > 0) {
      const { data: members } = await supabase
        .from('group_members')
        .select('user_id, group_id, member_name')
        .in('user_id', allUserIds)
        .in('group_id', groupIds);

      members?.forEach(m => {
        memberMap[`${m.user_id}_${m.group_id}`] = m.member_name;
      });
    }

    // For friends rounds (no group_id), fetch names from any group membership as fallback
    const friendsRoundUserIds = [...new Set(
      rounds?.filter(r => !r.group_id)
        .flatMap(r => r.round_invitations?.map(inv => inv.user_id) || []) || []
    )];

    let friendsNameMap = {};
    if (friendsRoundUserIds.length > 0) {
      const { data: friendMembers } = await supabase
        .from('group_members')
        .select('user_id, member_name')
        .in('user_id', friendsRoundUserIds);

      friendMembers?.forEach(m => {
        if (m.member_name && !friendsNameMap[m.user_id]) {
          friendsNameMap[m.user_id] = m.member_name;
        }
      });
    }

    // Attach member_name to each invitation
    rounds?.forEach(round => {
      round.round_invitations?.forEach(inv => {
        inv.member_name = memberMap[`${inv.user_id}_${round.group_id}`] || friendsNameMap[inv.user_id] || null;
      });
    });

    // Create a map of user's invitations for quick lookup
    const invitationMap = new Map(userInvitations.map(inv => [inv.round_id, inv]));

    // Process rounds to add user_invitation_status field and group name
    const processedRounds = rounds?.map(round => {
      // Get current user's invitation status from the map
      const userInvitation = invitationMap.get(round.id);

      // Add group name to the round object for easier access
      // Handle solo rounds (group_id = NULL) with appropriate label
      return {
        ...round,
        groupName: round.golf_groups?.name || (round.group_id ? 'Unknown Group' : 'Solo Round'),
        group_invite_code: round.golf_groups?.invite_code || null,
        user_invitation_status: userInvitation?.status || null
      };
    }) || [];

    return { data: processedRounds, error: null };
  } catch (error) {
    console.error('Error fetching rounds with invitation status:', error);
    return { data: [], error };
  }
};

/**
 * Respond to a round invitation, or change an existing response.
 *
 * Backed by the `respond_to_round_invitation` SECURITY DEFINER RPC rather than
 * a direct table update. The RPC holds the row lock that makes a
 * declined -> accepted flip safe on a capacity-limited open-invite round, and
 * it is the reason the permissive UPDATE policy on round_invitations could be
 * dropped (RLS WITH CHECK cannot see OLD, so it could not stop a requester
 * self-approving past the host).
 *
 * Resolves with an outcome rather than throwing, so callers can render inline
 * copy per case:
 *   updated | unchanged | not_invited | awaiting_host | round_full
 *   round_started | round_cancelled
 */
export const respondToRoundInvitation = async (roundId, status, notes = null) => {
  try {
    const { data, error } = await supabase.rpc('respond_to_round_invitation', {
      p_round_id: roundId,
      p_status: status,
      p_decline_reason: status === 'declined' ? notes : null
    });

    if (error) {
      console.error('Error responding to invitation:', error);
      throw error;
    }

    const result = data || { outcome: 'unknown' };

    // Calendar sync only when the answer actually moved. An unchanged or
    // rejected response must not touch the user's calendar.
    if (result.outcome === 'updated') {
      if (status === 'accepted') {
        recordPositiveAction('invite_accepted');
        try {
          await autoSyncRoundToCalendar(roundId);
        } catch (calendarError) {
          console.error('Failed to sync round to Outlook:', calendarError);
          // Don't throw - the response was recorded, calendar sync is optional
        }
      } else if (status === 'declined') {
        try {
          await deleteCalendarEvent(roundId);
        } catch (calendarError) {
          console.error('Failed to remove round from Outlook:', calendarError);
          // Don't throw - the response was recorded, calendar sync is optional
        }
      }
    }

    // In-app notification, email, and push for round responses are handled
    // server-side by DB trigger → send-round-response edge function. The
    // trigger fires on any status change now, not just the first response.
    return { data: result, error: null };
  } catch (error) {
    console.error('Error responding to invitation:', error);
    return { data: null, error };
  }
};

// Get user's round invitations with status
export const getUserRoundInvitations = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('round_invitations')
      .select(`
        *,
        rounds (
          *,
          golf_groups!rounds_group_id_fkey (
            name
          )
        )
      `)
      .eq('user_id', user.id)
      .order('invited_at', { ascending: false });

    if (error) {
      console.error('Error fetching user invitations:', error);
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching user invitations:', error);
    return { data: [], error };
  }
};

// UPDATED: Update round details with hole count
export const updateRound = async (roundId, updateData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First check if this is a solo round (no group_id)
    const { data: basicRound, error: basicError } = await supabase
      .from('rounds')
      .select('id, group_id, created_by')
      .eq('id', roundId)
      .single();

    if (basicError || !basicRound) {
      console.error('Error fetching round:', basicError);
      throw new Error('Round not found');
    }

    // Handle solo rounds - only creator can edit
    if (!basicRound.group_id) {
      if (basicRound.created_by !== user.id) {
        throw new Error('You do not have permission to edit this solo round');
      }
      // Solo round creator has permission - proceed to update
    } else {
      // Group round - check group membership and permissions
      const { data: roundData, error: roundError } = await supabase
        .from('rounds')
        .select(`
          *,
          golf_groups!rounds_group_id_fkey!inner(
            id,
            created_by,
            group_members(user_id, role)
          )
        `)
        .eq('id', roundId)
        .single();

      if (roundError) {
        console.error('Error fetching round for permission check:', roundError);
        throw new Error('Round not found or access denied');
      }

      // Find user's role in the group
      const userMembership = roundData.golf_groups.group_members?.find(
        member => member.user_id === user.id
      );

      if (!userMembership) {
        throw new Error('You are not a member of this group');
      }

      // Check permissions
      const isRoundCreator = roundData.created_by === user.id;
      const isGroupOwner = roundData.golf_groups.created_by === user.id;
      const isGroupAdmin = userMembership.role === 'admin';

      if (!isRoundCreator && !isGroupOwner && !isGroupAdmin) {
        throw new Error('You do not have permission to edit this round');
      }
    }

    // Update the round
    const { data, error } = await supabase
      .from('rounds')
      .update(updateData)
      .eq('id', roundId)
      .select()
      .single();

    if (error) {
      console.error('Error updating round:', error);
      throw error;
    }

    // Update Outlook calendar event if user has one
    try {
      console.log('📅 Round updated successfully, attempting to sync calendar event');
      // Check for any calendar event (not just 'synced' status - it could be 'pending' or had a previous failure)
      const { data: calendarEvent } = await supabase
        .from('teesync_calendar_events')
        .select('calendar_event_id, sync_status')
        .eq('round_id', roundId)
        .eq('user_id', user.id)
        .eq('calendar_provider', 'outlook')
        .maybeSingle();

      if (calendarEvent && calendarEvent.calendar_event_id) {
        console.log('📅 User has calendar event (status:', calendarEvent.sync_status, '), updating Outlook');
        await updateCalendarEvent(roundId);
      } else {
        console.log('⏭️  No calendar event found for this user/round');
      }
    } catch (calendarError) {
      console.error('⚠️  Failed to update Outlook calendar event:', calendarError);
      // Don't throw - round was updated successfully, calendar sync is optional
    }

    // Refresh weather if course, date, or time changed
    let weatherRefreshed = false;
    try {
      const courseChanged = updateData.course !== undefined;
      const dateChanged = updateData.date !== undefined;
      const timeChanged = updateData.time !== undefined;

      if (courseChanged || dateChanged || timeChanged) {
        console.log('🌤️ Round details changed, refreshing weather data');
        // Clear weather data if refreshing (in case new course has no coords)
        if (courseChanged) {
          await supabase
            .from('rounds')
            .update({
              course_latitude: null,
              course_longitude: null,
              weather_temperature: null,
              weather_wind_speed: null,
              weather_precipitation: null,
              weather_humidity: null,
              weather_uv_index: null,
              weather_alerts: null,
              weather_golf_score: null,
              weather_golf_category: null,
              weather_is_unplayable: false,
              weather_ground_category: null,
              weather_data_source: null,
              weather_accuracy: null,
              weather_fetch_error: null,
              course_website: null, // Clear cached website too
              course_phone: null, // Clear cached phone too
              golfnow_booking_url: null // Clear GolfNow link (course-specific)
            })
            .eq('id', roundId);

          // Refresh course website and phone when course changes
          try {
            if (data.course) {
              const { website, googleMapsUri, phone } = await getCourseWebsite(data.course);
              if (website || googleMapsUri || phone) {
                await supabase
                  .from('rounds')
                  .update({
                    course_website: website || googleMapsUri,
                    course_phone: phone
                  })
                  .eq('id', roundId);
              }
            }
          } catch (websiteError) {
            console.warn('⚠️ Failed to refresh course website (non-critical):', websiteError);
          }
        }
        // Fetch new weather data
        await fetchAndStoreRoundWeather(
          roundId,
          data.course,
          data.date,
          data.time,
          data.hole_count || 18
        );
        weatherRefreshed = true;
      }
    } catch (weatherError) {
      console.warn('⚠️ Weather refresh failed (non-critical):', weatherError);
      // Don't throw - round was updated successfully
    }

    // If weather was refreshed, re-fetch the round to include updated weather data
    if (weatherRefreshed) {
      const { data: updatedRound } = await supabase
        .from('rounds')
        .select('*')
        .eq('id', roundId)
        .single();

      if (updatedRound) {
        return { data: updatedRound, error: null };
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating round:', error);
    return { data: null, error };
  }
};

// Delete round with correct relationship path and enhanced debugging
export const deleteRound = async (roundId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First check if this is a solo round (no group_id). Also pull
    // timezone + coords so the cancellation snapshot can carry them through
    // to send-round-cancelled — emails need them for the "10:00 AM BST"
    // line and viewer-tz secondary line.
    const { data: basicRound, error: basicError } = await supabase
      .from('rounds')
      .select('id, group_id, created_by, date, time, timezone, course, course_latitude, course_longitude')
      .eq('id', roundId)
      .single();

    if (basicError || !basicRound) {
      console.error('Error fetching round:', basicError);
      throw new Error('Round not found');
    }

    // Handle solo rounds - only creator can delete
    if (!basicRound.group_id) {
      if (basicRound.created_by !== user.id) {
        throw new Error('You do not have permission to delete this solo round');
      }
      // Solo round creator has permission - proceed to delete
    } else {
      // Group round - check group membership and permissions
      const { data: roundData, error: fetchError } = await supabase
        .from('rounds')
        .select(`
          id,
          created_by,
          group_id,
          date,
          time,
          course,
          golf_groups!rounds_group_id_fkey!inner(
            id,
            name,
            created_by,
            group_members(user_id, role, member_name)
          )
        `)
        .eq('id', roundId)
        .single();

      if (fetchError) {
        console.error('Error fetching round for permission check:', fetchError);
        throw new Error(`Failed to fetch round details: ${fetchError.message}`);
      }

      if (!roundData) {
        throw new Error('Round not found');
      }

      // Find user's membership in the group
      const userMembership = roundData.golf_groups.group_members?.find(
        member => member.user_id === user.id
      );

      if (!userMembership) {
        throw new Error('You do not have access to this group');
      }

      // Check permissions
      const isRoundCreator = roundData.created_by === user.id;
      const isGroupOwner = roundData.golf_groups.created_by === user.id;
      const isGroupAdmin = userMembership.role === 'admin';

      if (!isRoundCreator && !isGroupOwner && !isGroupAdmin) {
        throw new Error('You do not have permission to delete this round');
      }
    }

    // Notify invited users about cancellation before deleting
    if (basicRound.group_id) {
      try {
        const { data: group } = await supabase
          .from('golf_groups')
          .select('name')
          .eq('id', basicRound.group_id)
          .single();

        const { error: cancelError } = await supabase
          .from('round_cancellations')
          .insert({
            round_id: roundId,
            actor_id: user.id,
            round_date: basicRound.date,
            round_time: basicRound.time,
            round_course: basicRound.course,
            group_id: basicRound.group_id,
            group_name: group?.name || null,
            round_timezone: basicRound.timezone ?? null,
            round_latitude: basicRound.course_latitude ?? null,
            round_longitude: basicRound.course_longitude ?? null,
          });

        if (cancelError) {
          console.error('Cancellation notification error:', cancelError);
        }
      } catch (notifyError) {
        console.error('Failed to send cancellation notifications:', notifyError);
        // Don't block deletion if notifications fail
      }
    }

    // Delete round invitations first
    const { data: deletedInvitations, error: invitationsError } = await supabase
      .from('round_invitations')
      .delete()
      .eq('round_id', roundId)
      .select('id');

    if (invitationsError) {
      console.error('Error deleting round invitations:', invitationsError);
      throw new Error(`Failed to delete round invitations: ${invitationsError.message}`);
    }

    // Delete calendar event if user has one (before deleting the round)
    try {
      console.log('🗑️  Round deletion initiated, checking for calendar event to remove');
      const { data: calendarEvent } = await supabase
        .from('teesync_calendar_events')
        .select('calendar_event_id, calendar_provider')
        .eq('round_id', roundId)
        .eq('user_id', user.id)
        .in('calendar_provider', ['outlook', 'google'])
        .maybeSingle();

      if (calendarEvent) {
        console.log(`🗑️  User has calendar event (${calendarEvent.calendar_provider}), removing from calendar`);
        await deleteCalendarEvent(roundId);
      } else {
        console.log('⏭️  No calendar event found for this user');
      }
    } catch (calendarError) {
      console.error('⚠️  Failed to delete calendar event:', calendarError);
      // Don't throw - proceed with round deletion even if calendar sync fails
    }

    // Delete the round
    const { data: deletedRound, error: deleteError } = await supabase
      .from('rounds')
      .delete()
      .eq('id', roundId)
      .select('id');

    if (deleteError) {
      console.error('Error deleting round:', deleteError);
      throw new Error(`Failed to delete round: ${deleteError.message}`);
    }

    if (!deletedRound || deletedRound.length === 0) {
      throw new Error('Round deletion failed - no rows affected');
    }

    return { data: true, error: null };
  } catch (error) {
    console.error('Error in deleteRound function:', error);
    return { data: null, error };
  }
};

// Debug function to check round permissions
export const debugRoundPermissions = async (roundId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: 'User not authenticated' };

    const { data, error } = await supabase
      .rpc('check_round_permissions', { round_uuid: roundId });

    if (error) {
      console.error('Error checking permissions:', error);
      return { error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in debugRoundPermissions:', error);
    return { error };
  }
};

/**
 * Toggle the booking status of a round
 * Only round creator or group owner can toggle booking status
 * @param {number} roundId - The round ID to update
 * @param {boolean} isBooked - The new booking status
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export const toggleBookingStatus = async (roundId, isBooked) => {
  try {
    const { data, error } = await supabase.rpc('toggle_round_booking', {
      p_round_id: roundId,
      p_is_booked: isBooked
    });

    if (error) {
      console.error('Error toggling booking status:', error);
      return { data: null, error };
    }

    if (!data.success) {
      return { data: null, error: new Error(data.error) };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception toggling booking status:', error);
    return { data: null, error };
  }
};

// Check round permissions for user with correct relationship path
export const checkRoundPermissions = async (roundId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { canEdit: false, canDelete: false };

    // Use correct relationship path
    const { data: roundData, error } = await supabase
      .from('rounds')
      .select(`
        *,
        golf_groups!rounds_group_id_fkey!inner(
          id,
          created_by,
          group_members(user_id, role)
        )
      `)
      .eq('id', roundId)
      .single();

    if (error || !roundData) {
      console.error('Error in checkRoundPermissions:', error);
      return { canEdit: false, canDelete: false };
    }

    // Find user's membership in the group
    const userMembership = roundData.golf_groups.group_members?.find(
      member => member.user_id === user.id
    );

    if (!userMembership) {
      return { canEdit: false, canDelete: false };
    }

    const isRoundCreator = roundData.created_by === user.id;
    const isGroupOwner = roundData.golf_groups.created_by === user.id;
    const isGroupAdmin = userMembership.role === 'admin';

    const canEdit = isRoundCreator || isGroupOwner || isGroupAdmin;
    const canDelete = isRoundCreator || isGroupOwner || isGroupAdmin;

    return {
      canEdit,
      canDelete,
      isRoundCreator,
      isGroupOwner,
      isGroupAdmin
    };
  } catch (error) {
    console.error('Error checking round permissions:', error);
    return { canEdit: false, canDelete: false };
  }
};

// ─── Open-invite rounds ──────────────────────────────────────────────────

/**
 * Apply the open-invite configuration to a newly-created round via the
 * `create_round_with_audience` RPC. Safe to call with `config.isOpenInvite=false`
 * - it just resets the round to a non-open state without notifications.
 *
 * @param {number} roundId
 * @param {{
 *   isOpenInvite: boolean,
 *   joinMode?: 'instant'|'review',
 *   maxPlayers?: number,
 *   audienceAllFriends?: boolean,
 *   friendIds?: string[],
 * }} config
 */
export const applyOpenInviteSettings = async (roundId, config) => {
  try {
    if (!roundId || !config) return { data: null, error: null };
    const { error } = await supabase.rpc('create_round_with_audience', {
      p_round_id: roundId,
      p_is_open_invite: !!config.isOpenInvite,
      p_join_mode: config.joinMode || 'review',
      p_max_players: config.maxPlayers || 4,
      p_audience_all_friends: !!config.audienceAllFriends,
      p_friend_ids: Array.isArray(config.friendIds) ? config.friendIds : [],
    });
    if (error) {
      console.error('Error applying open-invite settings:', error);
      return { data: null, error };
    }
    return { data: { ok: true }, error: null };
  } catch (err) {
    console.error('Exception in applyOpenInviteSettings:', err);
    return { data: null, error: err };
  }
};

/**
 * Fetch the list of open-invite rounds the current user is eligible to join.
 * Backed by `get_open_rounds_for_user` SECURITY DEFINER RPC.
 */
export const getOpenRoundsForUser = async ({ limit = 50, offset = 0 } = {}) => {
  try {
    const { data, error } = await supabase.rpc('get_open_rounds_for_user', {
      p_limit: limit,
      p_offset: offset,
    });
    if (error) {
      console.error('Error fetching open rounds:', error);
      return { data: [], error };
    }
    return { data: data || [], error: null };
  } catch (err) {
    console.error('Exception in getOpenRoundsForUser:', err);
    return { data: [], error: err };
  }
};

/**
 * Attempt to join an open-invite round. Server-side atomic: handles capacity,
 * audience-eligibility, and join_mode (instant vs review) in a single RPC.
 *
 * Returns one of: 'joined' | 'requested' | 'not_eligible' | 'full' | 'already_in'.
 */
export const joinOpenRound = async (roundId) => {
  try {
    const { data, error } = await supabase.rpc('attempt_join_open_round', {
      p_round_id: roundId,
    });
    if (error) {
      console.error('Error joining open round:', error);
      return { data: null, error };
    }
    const row = Array.isArray(data) ? data[0] : data;
    return { data: row || null, error: null };
  } catch (err) {
    console.error('Exception in joinOpenRound:', err);
    return { data: null, error: err };
  }
};

/**
 * Hide a broadcast (open-invite) round from the caller's "Rounds you can join"
 * list. Backed by `dismiss_open_round`, which also dismisses the matching
 * round_open_invite_created notification so the card and the bell agree.
 *
 * Returns one of: 'dismissed' | 'not_eligible'.
 */
export const dismissOpenRound = async (roundId) => {
  try {
    const { data, error } = await supabase.rpc('dismiss_open_round', {
      p_round_id: roundId,
    });
    if (error) {
      console.error('Error dismissing open round:', error);
      return { data: null, error };
    }
    return { data: data || null, error: null };
  } catch (err) {
    console.error('Exception in dismissOpenRound:', err);
    return { data: null, error: err };
  }
};

/**
 * Undo a dismissal. Backs the undo action on the dismiss toast.
 */
export const undismissOpenRound = async (roundId) => {
  try {
    const { data, error } = await supabase.rpc('undismiss_open_round', {
      p_round_id: roundId,
    });
    if (error) {
      console.error('Error restoring open round:', error);
      return { data: null, error };
    }
    return { data: data || null, error: null };
  } catch (err) {
    console.error('Exception in undismissOpenRound:', err);
    return { data: null, error: err };
  }
};

/**
 * Approve or decline a join request (creator-only). Backed by
 * `respond_to_join_request` RPC.
 */
export const respondToJoinRequest = async (invitationId, approve) => {
  try {
    const { error } = await supabase.rpc('respond_to_join_request', {
      p_invitation_id: invitationId,
      p_approve: !!approve,
    });
    if (error) {
      console.error('Error responding to join request:', error);
      return { data: null, error };
    }
    return { data: { ok: true }, error: null };
  } catch (err) {
    console.error('Exception in respondToJoinRequest:', err);
    return { data: null, error: err };
  }
};

/**
 * Fetch pending join requests for a round (creator-only view).
 * Returns invitation rows with requester profile info.
 */
export const getJoinRequestsForRound = async (roundId) => {
  try {
    const { data, error } = await supabase
      .from('round_invitations')
      .select(`
        id,
        round_id,
        user_id,
        status,
        invited_at,
        responded_at,
        source
      `)
      .eq('round_id', roundId)
      .eq('status', 'requested')
      .eq('source', 'join_request')
      .order('invited_at', { ascending: true });
    if (error) {
      console.error('Error fetching join requests:', error);
      return { data: [], error };
    }
    return { data: data || [], error: null };
  } catch (err) {
    console.error('Exception in getJoinRequestsForRound:', err);
    return { data: [], error: err };
  }
};